import { OrgType, QueryKey } from '@/enums';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import { useGetTenantQuery } from '@/hooks/useGetTenantQuery';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';

export const AuthContext = createContext<any>(null);

export const AuthProvider = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const queryClient = useQueryClient();

  const [auth, _setAuth] = useState<any>({
    ...JSON.parse(localStorage.getItem('auth') || '{}'),
  });

  const setAuth = useCallback(
    (value: any) => {
      const token = { ...auth, ...value };
      _setAuth(token);
      localStorage.setItem('auth', JSON.stringify(token));
    },
    [auth],
  );

  const { data: userFromQuery } = useGetMe();

  const { tenant: currentTenant } = useGetTenantQuery();

  // Nexus Support Launcher reads window.nexus.context fresh on every click
  // (see src/nexus.ts), so just keep it in sync with whoever's logged in —
  // no re-mount/update call needed, unlike the widget's icon.
  useEffect(() => {
    if (!window.nexus) return;
    const { email, firstName, lastName } = userFromQuery ?? {};
    const reporterName = [firstName, lastName].filter(Boolean).join(' ');
    window.nexus.context = {
      ...window.nexus.context,
      reporter_name: reporterName || undefined,
      reporter_email: email,
      project: currentTenant?.name,
    };
  }, [userFromQuery, currentTenant]);

  const handler = useCallback(() => {
    _setAuth({ ...JSON.parse(localStorage.getItem('auth') || '{}') });

    if (
      isEmpty(JSON.parse(localStorage.getItem('auth') || '{}')) &&
      !queryClient.isFetching({ queryKey: [QueryKey.SUB_DOMAIN] })
    ) {
      queryClient.refetchQueries({
        queryKey: [QueryKey.SUB_DOMAIN],
      });
    }
  }, [queryClient]);

  useEffect(() => {
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [handler]);

  // Support widget only shows for logged-in, non-Clinic users — re-applied
  // on every login/logout/tenant change, and also on 'nexus:ready' (see
  // nexus.ts) since the script loads async and NexusLauncher may not exist
  // yet on the first run here.
  useEffect(() => {
    const applyVisibility = () => {
      if (!window.NexusLauncher) return;
      const shouldShow =
        !!userFromQuery && currentTenant?.orgType !== OrgType.CLINIC;
      if (shouldShow) {
        window.NexusLauncher.show();
      } else {
        window.NexusLauncher.hide();
      }
    };
    applyVisibility();
    window.addEventListener('nexus:ready', applyVisibility);
    return () => window.removeEventListener('nexus:ready', applyVisibility);
  }, [userFromQuery, currentTenant]);

  return (
    <AuthContext.Provider value={{ auth, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
